"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Stethoscope,
  Sparkles,
  Crown,
  Heart,
  Compass,
  Globe2,
  Trophy,
  Building2,
  Briefcase,
  BookOpen,
  PenLine,
  Mic,
  ArrowRight,
  Brain,
  Activity,
  Scale,
  ShieldCheck,
  Target,
  Users,
  Zap,
  Award,
  Layers,
  GitBranch,
  Rocket,
  GraduationCap,
  Flag,
  Lock,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const HEADLINE_STATS = [
  { metric: "15", suffix: "/18", label: "Campaign Victories", sub: "Verified across 18 engagements", accent: "red" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "Across 22 official languages", accent: "purple" },
  { metric: "300", suffix: "+", label: "Elite Clients Served", sub: "Nine client categories", accent: "cyan" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "amber-gold" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 seconds max/year", accent: "denim" },
];

const EARLY_LIFE = [
  {
    icon: GraduationCap,
    title: "Delhi — Where the Discipline Was Forged",
    body: "Delhi is not a gentle teacher. The healthcare ecosystem alone — serving a population of hundreds of millions with finite resources — instills a discipline that cannot be learned in any classroom. Resources are limited. Time is scarce. The demand exceeds capacity by orders of magnitude. The physician who survives this environment learns to maximize outcomes with whatever is available, to decide quickly and correctly, and to carry the weight of those decisions without hesitation.",
  },
  {
    icon: Stethoscope,
    title: "MBBS with Specialization in Gynaecology",
    body: "The medical degree was not a professional qualification — it was the installation of an operating system for the mind. Evidence over intuition. Protocol under pressure. Triage when everything screams urgent. Accountability not for effort but for outcome. The gynaecology specialization added the intimate understanding of women's health that would later produce The 70% Project.",
  },
  {
    icon: ShieldCheck,
    title: "Credibility Under Fire",
    body: "The years of clinical practice built something no credential can confer. When Dr. Jyoti Kush later entered board rooms where the stakes were national, the quiet authority of someone who had managed life-and-death decisions — not in theory, but in practice — was carried forward. This authority is not claimed. It is recognized. It is the foundation upon which every subsequent engagement has been built.",
  },
];

const CLINICAL_PRINCIPLES = [
  {
    num: "01",
    title: "Differential Diagnosis Before Action",
    body: "A physician does not treat the first symptom. The discipline is to ask: what else could this be? Most organizational failures trace to leaders who treated the symptom while the disease progressed undetected. The clinical method prevents this.",
    accent: "red",
  },
  {
    num: "02",
    title: "Triage Under Absolute Pressure",
    body: "When every patient screams urgency, the physician must rank: what is fatal now, what can wait, what must be monitored, what can be safely ignored. This is not time management. It is survival architecture applied to sovereign-scale operations.",
    accent: "purple",
  },
  {
    num: "03",
    title: "Protocol with Judgment",
    body: "Protocols carry through the ninety-five percent of cases that follow the pattern. The remaining five percent require clinical judgment: the ability to deviate from protocol when the situation demands it. The platforms run on protocol. The operator keeps the judgment.",
    accent: "cyan",
  },
  {
    num: "04",
    title: "Outcome Accountability",
    body: "A physician does not receive credit for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Dr. Kush restores it — every engagement is measured by verified outcomes, not effort narratives.",
    accent: "emerald",
  },
  {
    num: "05",
    title: "Composure as Infrastructure",
    body: "The delivery room teaches that the senior person's state determines the team's state. Tension propagates downward. Panic cascades. The discipline of maintaining internal composure so that the team can function is not emotional intelligence — it is operational infrastructure at sovereign scale.",
    accent: "amber-gold",
  },
  {
    num: "06",
    title: "Do No Harm",
    body: "The oldest principle in medicine. Under pressure, most leaders make the situation worse — adding complexity, introducing noise, creating side effects that compound the original problem. The discipline of not making it worse is the most underrated operational skill in leadership.",
    accent: "denim",
  },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const PHASES = [
  {
    num: "01",
    title: "Information Privacy",
    body: "The foundation. Encrypted private networks for governments and their highest offices. The kind of work where a single vulnerability can compromise national stability. Zero security incidents across every engagement — the cultural DNA established here fueled every subsequent expansion.",
    accent: "red",
  },
  {
    num: "02",
    title: "Intelligence & Listening",
    body: "Network Monitoring, Digital Listening, and Intelligence Analysis — not as products, but as operational necessities demanded by clients. The transition from defensive security to proactive intelligence gave the organization foresight: the ability to see not just what was happening, but what was about to happen.",
    accent: "purple",
  },
  {
    num: "03",
    title: "Perception, Governance & Political Strategy",
    body: "Existing clients invited the organization to apply its methodologies to broader challenges: Perception Management, Governance Support, and country-level Political Campaigns. The Penta-P framework crystallized here — five interconnected domains operating as a closed-loop system.",
    accent: "cyan",
  },
  {
    num: "04",
    title: "The Digital Conglomerate",
    body: "The evolution into MaxiMize Infinium — the World's First Digital Conglomerate for Strategic Sovereignty. Sixty-plus integrated services across five interconnected domains. Eighteen countries. Three continents. Over three hundred elite clients served. And it grew, excelled, and evolved even during the COVID era.",
    accent: "emerald",
  },
];

const TRIPLE_THREAT = [
  {
    icon: Stethoscope,
    title: "Medicine",
    body: "Provides the method. Clinical training instills a discipline that business schools do not teach: evidence over intuition, protocol under pressure, triage when everything is urgent, and accountability not for effort but for outcome. The six principles form the actual operating system.",
    accent: "red",
    metric: "MBBS",
    metricLabel: "Gynaecology · Delhi",
  },
  {
    icon: Layers,
    title: "Technology",
    body: "Provides the scale. The integrated platform ecosystem, built in-house over fifteen-plus years, processes millions of data points, monitors two hundred-plus digital platforms, and orchestrates operations across continents in under an hour. Zero third-party dependencies.",
    accent: "cyan",
    metric: "60+",
    metricLabel: "Integrated services",
  },
  {
    icon: Globe2,
    title: "Politics",
    body: "Provides the stakes. When the client is a sovereign government, a royal household, a defense ministry, or a political leader facing an existential contest, the work is not theoretical. Outcomes shape nations, economies, and the lives of millions.",
    accent: "amber-gold",
    metric: "900M",
    metricLabel: "Voters reached",
  },
];

const DIFFERENTIATORS = [
  {
    icon: Brain,
    title: "The Medical Method",
    body: "The only operator at sovereign scale whose discipline was forged in clinical practice. Six clinical principles applied across eighteen countries and the integrated platform ecosystem. This is not a leadership metaphor — it is a clinical operating system.",
    accent: "red",
  },
  {
    icon: Trophy,
    title: "A Decade of Building",
    body: "Not a portfolio of short engagements. A single organization, co-built and sustained for ten years, through every global disruption. The infrastructure that took a decade to build cannot be replicated without that same decade of investment. This is the moat.",
    accent: "purple",
  },
  {
    icon: Heart,
    title: "The 70% Operating Model",
    body: "A decade of evidence that the world's most overlooked talent pool produces the most reliable operational outcomes. The argument from operations, not morality. Fifteen campaign victories. Over three hundred elite clients. Zero security incidents.",
    accent: "emerald",
  },
  {
    icon: Crown,
    title: "Sovereign-Scale Experience",
    body: "Operations at the level where stakes are national, clients are sovereign, and failure is measured in stability. Thirty-plus countries. Three continents. The operational discipline required at this level is the same discipline learned in a delivery room.",
    accent: "amber-gold",
  },
  {
    icon: Lock,
    title: "Zero-Dependency Architecture",
    body: "Every platform owned. Every line of code controlled. Every capability proprietary. No external platform, no off-the-shelf solution, no licensed technology contributes to the core capabilities. Integration is the moat.",
    accent: "denim",
  },
  {
    icon: Compass,
    title: "Cross-Domain Mastery",
    body: "Medicine, technology, and politics in one operator. The intersection is where complex, multi-stakeholder, high-stakes problems are solved. No other executive at this level occupies this intersection.",
    accent: "coral",
  },
];

const AUDIENCES = [
  {
    icon: Flag,
    title: "Governments & Heads of State",
    body: "Seeking operational excellence, governance transformation, and strategic sovereignty. When political authority must be converted into public outcomes — citizen engagement, policy implementation, service delivery — the operational infrastructure delivers.",
    accent: "red",
  },
  {
    icon: Crown,
    title: "Royal Families & Dynasties",
    body: "Reputation protection, privacy enforcement, and legacy narrative management with absolute discretion. National security level confidentiality. Zero security incidents across fifteen-plus years.",
    accent: "purple",
  },
  {
    icon: Target,
    title: "Political Leaders & Organizations",
    body: "Voter intelligence, campaign engineering, and verified electoral outcomes. Fifteen victories out of eighteen engagements. The operational philosophy: when citizens can answer what is in it for them, and government can perform, governance wins.",
    accent: "cyan",
  },
  {
    icon: ShieldCheck,
    title: "Defense & Intelligence Agencies",
    body: "Secure communications, threat detection, intelligence fusion, and cyber forensics. Seven-layer sovereign security. Quantum-resistant encryption. Zero third-party dependencies.",
    accent: "emerald",
  },
  {
    icon: Building2,
    title: "Fortune-Scale Corporations",
    body: "Brand protection, crisis response, competitive positioning, and operational transformation. The same sovereign-grade infrastructure deployed for governments — not a diluted version.",
    accent: "amber-gold",
  },
  {
    icon: Users,
    title: "Aspiring Women Leaders",
    body: "A role model who has built, not just advocated — who demonstrates through operational results that the best operators are the ones the world overlooks. The 70% Project is the evidence.",
    accent: "coral",
  },
];

const GOALS = [
  {
    icon: Target,
    title: "Establish Operational Excellence as a Discipline",
    body: "Move excellence from the poster to the engine room — making it repeatable, accountable, and verifiable. The five-step methodology applied across every engagement.",
    accent: "red",
  },
  {
    icon: Users,
    title: "Advance Women in Leadership Through Evidence",
    body: "Prove, through The 70% Project and a decade of operating data, that the talent the world overlooks is the talent that outperforms. The case made from operations, not morality.",
    accent: "emerald",
  },
  {
    icon: GitBranch,
    title: "Bridge Medicine, Technology, and Politics",
    body: "Demonstrate that clinical rigor, applied to technological systems under political-grade stakes, produces outcomes no single-domain specialist can achieve. The triple threat as one method.",
    accent: "cyan",
  },
  {
    icon: Scale,
    title: "Drive Governance Transformation",
    body: "Convert political authority into measurable public outcomes through digital governance, citizen engagement, and policy implementation. Good governance always is the best governance.",
    accent: "purple",
  },
  {
    icon: Rocket,
    title: "Build Organizations That Last",
    body: "Construct consequential institutions through systems, not charisma — measured by outcomes, not publicity. The Create-Achieve-Innovate cycle never stops. From small to large. From imaginable to unimaginable.",
    accent: "amber-gold",
  },
];

const WRITING = [
  {
    icon: PenLine,
    title: "Excellence Is an Operating System, Not a Value",
    body: "The thesis essay. Why values statements fail and systems succeed, and what an operating system for excellence actually contains. If the operating steps cannot be written down, the outcome is luck, not excellence.",
    accent: "red",
  },
  {
    icon: Stethoscope,
    title: "Everything About Leadership, Learned in a Delivery Room",
    body: "The signature essay on the medical method as leadership. Diagnosis, triage, regulation of the room, protocol and judgment, outcome accountability, and the discipline of doing no harm. The delivery room is the most demanding leadership environment on earth.",
    accent: "purple",
  },
  {
    icon: Heart,
    title: "Why Teams Built Seventy Percent Women",
    body: "The flagship essay. Not a values statement — an operating decision with a decade of evidence. The operating advantages: communication, risk discipline, collaboration, retention, ego management. The world's most overlooked talent pool produces the most reliable outcomes.",
    accent: "emerald",
  },
  {
    icon: BookOpen,
    title: "The Operator's Notebook",
    body: "The biweekly publication. One short, disciplined note on how excellent things run. No filler. No slogans. One principle, made usable, every two weeks. The operating principles that govern the integrated ecosystem — distilled into something applicable to any organization.",
    accent: "cyan",
  },
  {
    icon: Award,
    title: "The 70% Project",
    body: "The flagship initiative documenting the operating case for the talent the world overlooks. From evidence to movement — from documenting the operating case to building the infrastructure that makes it the default.",
    accent: "amber-gold",
  },
];

export default function AboutHubPage() {
  return (
    <>
      <PageHero
        icon={Crown}
        overline="About Dr. Jyoti Kush"
        overlineClassName="text-red"
        title={
          <>
            Physician. Operator. <span className="text-gradient-premium">Architect of Sovereign-Scale Operations.</span>
          </>
        }
        subtitle="The Chief Operating Officer of CryptoMize and MaxiMize Infinium — a physician-turned-executive whose career defies every conventional category. This is the full story of how a gynaecologist from Delhi became the operational architect of a sovereign-grade digital conglomerate spanning eighteen countries."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About" }]}
        badge="A decade of verified sovereign-scale delivery"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              A career that defies every conventional category.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush holds an MBBS with a specialization in Gynaecology from Delhi. The clinical discipline — diagnose before treating, triage under pressure, follow protocol while preserving judgment, own the outcome, do no harm — is the same discipline applied to the operational leadership of a digital conglomerate serving governments, royal families, political leaders, defense agencies, and Fortune-scale corporations across thirty-plus countries.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              As COO, the operational mandate spans sixty-plus integrated services across five interconnected domains — Perception, Privacy, Politics, Policing, Policy — and cross-continental teams operating across eighteen countries. The work is not administrative. It is the operational execution layer that translates technological capability into client outcomes.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The teams are over seventy percent women — a statistic that emerged not from policy but from performance. The thought leadership, through The 70% Project and The Operator&apos;s Notebook, documents the operating case for the talent the world overlooks.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <GraduationCap className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Early Life &amp; Medical Training</span>
          </div>
          <h2 className="text-display-md mb-4">Where the discipline was forged.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The story begins in Delhi, where a young woman entered medical school with the precision and determination that would later define an operational career. The medical training was the installation of an operating system for the mind.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {EARLY_LIFE.map((e, i) => (
            <motion.div
              key={e.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card rounded-2xl p-6 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20"
            >
              <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg">
                <e.icon className="size-6 text-white" />
              </div>
              <h3 className="text-heading-md mb-3 text-cyan">{e.title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{e.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Clinical Operating System</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles learned in a delivery room.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The operating method is not a leadership philosophy borrowed from a business school curriculum. It is a clinical operating system — six principles drilled into muscle memory through years of medical practice.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLINICAL_PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Activity className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Decade of Building</span>
          </div>
          <h2 className="text-display-md mb-4">Four phases. One operational constant.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            CryptoMize was not an arrival. It was a co-construction — a decade-long evolution from a security-focused consultancy into a full-spectrum Digital Conglomerate. Dr. Jyoti Kush has been the operational constant across every phase.
          </p>
        </div>
        <div className="space-y-4">
          {PHASES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <span className="text-display-sm font-bold text-white">{p.num}</span>
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Phase {p.num}</span>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                  <div className="hidden lg:block text-right shrink-0 max-w-[200px]">
                    <div className={`text-caption font-semibold ${a.text}`}>Operational discipline applied at increasing scale</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Zap className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Triple Threat</span>
          </div>
          <h2 className="text-display-md mb-4">The intersection no one else occupies.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Most leaders specialize in a single domain. Dr. Jyoti Kush operates across three — and the intersection is where the value is unmatched.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TRIPLE_THREAT.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <t.icon className="size-6 text-white" />
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <h3 className={`text-display-sm ${a.text}`}>{t.metric}</h3>
                    <span className="text-caption text-muted-foreground">{t.metricLabel}</span>
                  </div>
                  <h4 className="text-heading-md mb-2 mt-3">{t.title}</h4>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{t.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Heart className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The 70% Operating Model</span>
          </div>
          <h2 className="text-display-md mb-4">Building teams that outperform by hiring the talent others overlook.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The teams are, in most offices, about seventy percent women. People assume this is a values statement. It is not. It is an operating decision with a decade of evidence behind it.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { num: "01", title: "Communication", body: "Problems surface earlier because team members are willing to say the uncomfortable thing before it becomes a catastrophe." },
            { num: "02", title: "Risk Discipline", body: "Higher adherence to protocol without sacrificing the judgment needed for exceptional situations." },
            { num: "03", title: "Collaboration", body: "Internal energy goes outward, toward the mission, instead of inward, toward each other." },
            { num: "04", title: "Retention", body: "Institutional knowledge preserved. The environment is one where people can stay and thrive." },
            { num: "05", title: "Ego Management", body: "Ego interferes less with execution, allowing operational systems to function as designed." },
          ].map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-lime/10 to-transparent border border-lime/20"
            >
              <div className="text-display-sm font-bold text-lime/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-lime">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            href="/about/philosophy/"
            className="inline-flex items-center gap-2 text-caption font-semibold text-lime hover:gap-3 transition-all"
          >
            Read the full philosophy on the 70% Operating Model <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Trophy className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Core Differentiators</span>
          </div>
          <h2 className="text-display-md mb-4">What makes Dr. Jyoti Kush unmatched.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six structural advantages — verified through fifteen-plus years of sovereign-scale operations, fifteen campaign victories, and zero security incidents.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DIFFERENTIATORS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <d.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{d.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Briefcase className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Ideal Target Audience</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from the engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The people and organizations that engage Dr. Jyoti Kush — from sovereign-level clients to aspiring leaders seeking a role model who has built, not just advocated.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
              <Target className="size-3.5 text-red" />
              <span className="text-overline text-red">Goals &amp; Future</span>
            </div>
            <h2 className="text-display-md mb-4">What Dr. Jyoti Kush aims to achieve.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Excellence is not a value to be placed on walls. It is a system to be embedded in architecture. The goals below are not aspirations — they are the operating objectives that govern every engagement, every platform, and every team.
            </p>
          </div>
          <div className="space-y-3">
            {GOALS.map((g, i) => {
              const a = ACCENT[g.accent];
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3.5">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <g.icon className="size-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{g.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <BookOpen className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Selected Writing &amp; Insights</span>
          </div>
          <h2 className="text-display-md mb-4">The body of work.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The clinical method applied to organizational leadership — documented through essays, a biweekly publication, and a flagship initiative. Not marketing material. The documentation of an operating system proven across eighteen countries.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WRITING.slice(0, 6).map((w, i) => {
            const a = ACCENT[w.accent];
            return (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <w.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{w.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{w.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Speaking engagements, executive advisory, board service, and operational consulting. Every engagement governed by the five-step methodology. Every outcome verified. National security level confidentiality from first contact.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/credentials/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Award className="size-4" />
                View Credentials
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Biography", description: "The full arc — from medicine to sovereign-scale operations.", href: "/about/biography/", icon: Stethoscope, accent: "red" },
          { title: "Journey", description: "Four phases. Physician, privacy, decade, operator.", href: "/about/journey/", icon: Compass, accent: "purple" },
          { title: "Credentials", description: "Medical foundation and operational track record.", href: "/about/credentials/", icon: Award, accent: "emerald" },
          { title: "Recognition", description: "Nobel Peace Prize, WEF, and sovereign stages.", href: "/about/recognition/", icon: Trophy, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
